import React, { useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';

const LANG_OPTIONS = [
  { value: 'al', label: 'Shqip', code: 'AL', flag: 'AL' },
  { value: 'en', label: 'English', code: 'EN', flag: 'EN' },
  { value: 'it', label: 'Italiano', code: 'IT', flag: 'IT' },
];

const PublicLanguageSwitch = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const currentLanguage = useMemo(() => {
    const lang = (i18n.resolvedLanguage || i18n.language || 'al').toLowerCase();
    return LANG_OPTIONS.find((item) => item.value === lang) ?? LANG_OPTIONS[0];
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const changeLanguage = (nextLanguage: string) => {
    i18n.changeLanguage(nextLanguage);
    setIsOpen(false);
  };

  return (
    <div className="theme-dropdown public-lang-switcher" ref={rootRef}>
      <button
        type="button"
        className="theme-dropdown-trigger theme-dropdown-trigger--lang public-lang-trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('generic.language', { defaultValue: 'Language' })}
      >
        <span className="landing-lang-flag">
          <ReactCountryFlag svg countryCode={currentLanguage.flag} />
        </span>
        <KeyboardArrowDownRoundedIcon fontSize="inherit" />
      </button>

      {isOpen ? (
        <div className="theme-dropdown-menu landing-language-menu public-lang-menu" role="menu">
          {LANG_OPTIONS.map((option) => {
            const isActive = option.value === currentLanguage.value;

            return (
              <button
                key={option.value}
                type="button"
                className={`theme-dropdown-item landing-language-item public-lang-item ${isActive ? 'active' : ''}`}
                onClick={() => changeLanguage(option.value)}
                role="menuitemradio"
                aria-checked={isActive}
              >
                <span className="landing-lang-flag">
                  <ReactCountryFlag svg countryCode={option.flag} />
                </span>
                <span className="landing-lang-item-label">{option.label}</span>
                <span className="landing-lang-item-code">{option.code}</span>
                {isActive ? <CheckRoundedIcon fontSize="inherit" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default PublicLanguageSwitch;
